// The extra-instruction tools of the Studio Editor = the Bulk Photo Edit tools (backend config/bulkPhotoThemes.js, served by
// GET /api/bulk-photo/themes): a category tile grid, the category's tools as a list of rows, a step slider (shown once a
// leveled tool is on), and a chip row of everything picked (so tools chosen in another category stay visible). The page owns the value.
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconLoader2, IconX } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import bulkPhotoService from '../../../services/bulkPhotoService';
import { toBnDigits } from '../../../utils/bnFormat';
import { categoryIcon, toolIcon } from '../../../constants/bulkToolIcons';

// The studio's own background step paints the backdrop, so the bulk "Photo Background" tools would fight it — left out here.
const HIDDEN_CATEGORIES = ['background'];
// …and the bulk "dress colour change" tool: the studio's dress section already changes the outfit and its colour.
const HIDDEN_TOOLS = ['dress-color-change'];
// The bulk editor's three intensity steps (the backend also knows "natural", offered only to a tool that lists its own steps below).
const LEVEL_UI = ['subtle', 'medium', 'strong'];
// Tools that read differently here than in the bulk editor. Background Blur lives in the hidden "Photo Background" category, but it keeps
// the photo's own background instead of fighting the backdrop step, so it is offered under Studio Image with its own steps.
const STUDIO_TOOL_OVERRIDES = { 'bg-blur': { category: 'studio', uiLevels: ['natural', 'medium', 'strong'] } };

// One fetch per page load — the tool list is static config on the server.
let themesPromise = null;
const loadThemes = () => {
    if (!themesPromise) {
        themesPromise = bulkPhotoService.getThemes()
            .then((r) => ({ categories: r.data?.categories || [], themes: (r.data?.themes || []).map((th) => ({ ...th, ...STUDIO_TOOL_OVERRIDES[th.id] })) }))
            .catch((e) => { themesPromise = null; throw e; });
    }
    return themesPromise;
};

/**
 * Picked tools as the request sends them: `id` or `id:level` (leveled tools only), in the order they were picked.
 * `value` is `{ [toolId]: level | null }`.
 */
export const bulkToolsPayload = (value) => Object.entries(value || {}).map(([id, level]) => (level ? `${id}:${level}` : id));

/** The inverse of bulkToolsPayload — a saved record's list back into the panel's value. */
export const bulkToolsFromList = (list) => {
    const out = {};
    (Array.isArray(list) ? list : []).forEach((entry) => {
        const [id, level] = String(entry || '').split(':');
        if (/^[a-z0-9-]{2,40}$/.test(id)) out[id] = LEVEL_UI.includes(level) || level === 'natural' ? level : null;
    });
    return out;
};

/**
 * @param value     `{ [toolId]: level | null }`
 * @param onChange  (nextValue) => void
 * @param busy      a generation is running — nothing can change
 */
export default function StudioToolsPanel({ value, onChange, busy = false }) {
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const num = (n) => (isBn ? toBnDigits(n) : String(n));
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    const [activeCat, setActiveCat] = useState(null);

    useEffect(() => {
        let alive = true;
        loadThemes()
            .then((d) => { if (alive) setData(d); })
            .catch(() => { if (alive) setFailed(true); });
        return () => { alive = false; };
    }, []);

    const categories = useMemo(() => (data?.categories || []).filter((c) => !HIDDEN_CATEGORIES.includes(c.id)), [data]);
    const toolsById = useMemo(() => new Map((data?.themes || []).map((th) => [th.id, th])), [data]);
    const cat = activeCat || categories[0]?.id;
    const catTools = (data?.themes || []).filter((th) => th.category === cat && !HIDDEN_TOOLS.includes(th.id));
    const name = (x) => (x ? (isBn ? x.nameBn || x.name : x.name) : '');
    const levelLabel = (lvl) => t(`bulkPhotoEdit.create.level.${lvl}`);
    const picked = Object.keys(value || {}).filter((id) => toolsById.has(id) && !HIDDEN_TOOLS.includes(id));
    const countIn = (catId) => picked.filter((id) => toolsById.get(id)?.category === catId).length;

    const toggle = (tool) => {
        if (busy) return;
        const next = { ...value };
        if (tool.id in next) {
            delete next[tool.id];
        } else {
            next[tool.id] = tool.levels?.length ? (tool.defaultLevel || 'medium') : null;
        }
        onChange(next);
    };
    const remove = (id) => { const next = { ...value }; delete next[id]; onChange(next); };
    // Select / clear every tool of the open category (the other categories keep their picks).
    const selectAllInCat = () => {
        if (busy) return;
        const next = { ...value };
        catTools.forEach((tool) => { if (!(tool.id in next)) next[tool.id] = tool.levels?.length ? (tool.defaultLevel || 'medium') : null; });
        onChange(next);
    };
    const clearCat = () => {
        if (busy) return;
        const next = { ...value };
        catTools.forEach((tool) => { delete next[tool.id]; });
        onChange(next);
    };

    if (failed) return <p className="text-xs text-red-600 dark:text-red-400">{t('studioEditor.toolsLoadFailed')}</p>;
    if (!data) {
        return (
            <div className="flex items-center gap-2 py-4 text-xs text-zinc-400">
                <IconLoader2 size={16} className="animate-spin" /> {t('common.loading')}
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Categories — all six visible as a tile grid (no hidden sideways scroll) */}
            <div className="grid grid-cols-3 gap-1.5" role="tablist">
                {categories.map((c) => {
                    const Icon = categoryIcon(c.id);
                    const active = c.id === cat;
                    const n = countIn(c.id);
                    return (
                        <button
                            key={c.id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setActiveCat(c.id)}
                            className={cn(
                                'relative flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-lg border px-1 py-1.5 text-center transition-colors',
                                active
                                    ? 'border-primary bg-primary text-white shadow-sm shadow-primary/30'
                                    : 'border-zinc-200 bg-white text-zinc-600 hover:border-primary/50 hover:text-primary dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
                            )}
                        >
                            <Icon size={17} strokeWidth={1.8} />
                            <span className="line-clamp-2 text-[11px] font-semibold leading-tight">{name(c)}</span>
                            {n > 0 && (
                                <span className={cn('absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold', active ? 'bg-white text-primary' : 'bg-primary text-white')}>{num(n)}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Select / clear the open category */}
            <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">
                    {name(categories.find((c) => c.id === cat))} · {num(countIn(cat))}/{num(catTools.length)}
                </span>
                <div className="ml-auto flex items-center gap-1">
                    <button
                        type="button"
                        disabled={busy || countIn(cat) === catTools.length}
                        onClick={selectAllInCat}
                        className="rounded-md px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        {t('studioEditor.selectAll')}
                    </button>
                    <button
                        type="button"
                        disabled={busy || countIn(cat) === 0}
                        onClick={clearCat}
                        className="rounded-md px-2 py-1 text-[11px] font-semibold text-zinc-500 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-zinc-400 dark:hover:bg-zinc-800"
                    >
                        {t('studioEditor.clearAll')}
                    </button>
                </div>
            </div>

            {/* The category's tools — one row each, a checkbox to switch it on; a leveled tool then shows a step slider for its strength. */}
            <div className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200 dark:divide-zinc-800 dark:border-zinc-700">
                {catTools.map((tool) => {
                    const Icon = toolIcon(tool.id);
                    const on = tool.id in (value || {});
                    const levels = tool.uiLevels || LEVEL_UI.filter((l) => tool.levels?.includes(l));
                    const level = value?.[tool.id] || tool.defaultLevel || 'medium';
                    const levelIdx = Math.max(0, levels.indexOf(level));
                    return (
                        <div key={tool.id} className={cn('transition-colors', on ? 'bg-primary/5' : 'bg-white dark:bg-zinc-900')}>
                            <button
                                type="button"
                                disabled={busy}
                                aria-pressed={on}
                                onClick={() => toggle(tool)}
                                className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-zinc-800/60"
                            >
                                <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors', on ? 'bg-primary text-white' : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-300')}>
                                    <Icon size={17} />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className={cn('block text-sm font-medium leading-tight', on ? 'text-primary' : 'text-zinc-800 dark:text-zinc-100')}>{name(tool)}</span>
                                    {!isBn && tool.desc && <span className="block truncate text-xs text-zinc-500 dark:text-zinc-400">{tool.desc}</span>}
                                </span>
                                <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors', on ? 'border-primary bg-primary text-white' : 'border-zinc-300 dark:border-zinc-600')}>
                                    {on && <IconCheck size={12} strokeWidth={3.5} />}
                                </span>
                            </button>
                            {/* Strength — a step slider, shown once the tool is on */}
                            {on && levels.length > 1 && (
                                <div className="w-[calc(3.5rem+150px)] px-3 pb-3 pl-14">
                                    <input
                                        type="range"
                                        min={0}
                                        max={levels.length - 1}
                                        step={1}
                                        value={levelIdx}
                                        disabled={busy}
                                        aria-label={name(tool)}
                                        aria-valuetext={levelLabel(levels[levelIdx])}
                                        onChange={(e) => onChange({ ...value, [tool.id]: levels[Number(e.target.value)] })}
                                        className="block h-1.5 w-full cursor-pointer accent-primary disabled:cursor-not-allowed"
                                    />
                                    <div className="mt-1 flex justify-between">
                                        {levels.map((l, i) => (
                                            <button
                                                key={l}
                                                type="button"
                                                disabled={busy}
                                                onClick={() => onChange({ ...value, [tool.id]: l })}
                                                className={cn('text-[11px] transition-colors', i === levelIdx ? 'font-bold text-primary' : 'font-medium text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300')}
                                            >
                                                {levelLabel(l)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Everything picked, across categories */}
            {picked.length > 0 && (
                <div>
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{t('studioEditor.toolsPicked', { count: num(picked.length) })}</p>
                    <div className="flex flex-wrap gap-1.5">
                        {picked.map((id) => {
                            const tool = toolsById.get(id);
                            return (
                                <span key={id} className="inline-flex items-center gap-1 rounded-full bg-primary/10 py-0.5 pl-2 pr-1 text-[11px] font-medium text-primary">
                                    {name(tool)}
                                    {value[id] && <span className="text-primary/70">· {levelLabel(value[id])}</span>}
                                    <button type="button" disabled={busy} onClick={() => remove(id)} aria-label={t('common.remove')} className="rounded-full p-0.5 hover:bg-primary/20">
                                        <IconX size={11} />
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
